import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Outlet, useParams } from "react-router-dom";
import Layout from "./components/Layout";
import RouteTitle from "./components/RouteTitle";
import Home from "./pages/Home";
import Policies from "./pages/Policies";
import FAQ from "./pages/FAQ";
import ServiceDetail from "./pages/ServiceDetail";
import Checkout from "./pages/Checkout";
import BookingConfirmation from "./pages/BookingConfirmation";
import AboutMore from "./pages/about-more";
import Resources from "./pages/Resources";
import InHomeArtTherapy from "./pages/InHomeArtTherapy";
import Packages from "./pages/Packages";
import GroupsWorkshops from "./pages/GroupsWorkshops";
import Organizations from "./pages/Organizations";
import Schools from "./pages/Schools";
import NotFound from "./pages/NotFound";
import AdminGuard from "./pages/admin/AdminGuard";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminBookings from "./pages/admin/AdminBookings";
import AdminMessages from "./pages/admin/AdminMessages";
import ContentHome from "./pages/admin/content/ContentHome";
import CollectionScreen from "./pages/admin/content/CollectionScreen";
import ItemEditorRoute from "./pages/admin/content/ItemEditor";
import MediaLibrary from "./pages/admin/content/MediaLibrary";
import { SERVICES } from "./data/services";
import "./App.css";

const SITE_NAME = "Healing Hues";
const pageTitle = (page) => `${page} | ${SITE_NAME}`;

function ServiceRoute() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  return (
    <RouteTitle title={pageTitle(service ? service.title : "Service not found")}>
      <ServiceDetail />
    </RouteTitle>
  );
}

/**
 * Owner pages: titled, kept out of search indexes (robots noindex added to
 * <head> while mounted), and rendered without the public site chrome.
 * Nothing on the public site links here.
 */
function AdminRoute({ title, children }) {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    meta.setAttribute("data-admin-noindex", "");
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  return <RouteTitle title={pageTitle(title)}>{children}</RouteTitle>;
}

function PublicLayout() {
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/admin/login" element={<AdminRoute title="Owner sign in"><AdminLogin /></AdminRoute>} />
        <Route path="/admin" element={<AdminRoute title="Bookings"><AdminGuard><AdminBookings /></AdminGuard></AdminRoute>} />
        <Route path="/admin/messages" element={<AdminRoute title="Messages"><AdminGuard><AdminMessages /></AdminGuard></AdminRoute>} />
        <Route path="/admin/content" element={<AdminRoute title="Site content"><AdminGuard><ContentHome /></AdminGuard></AdminRoute>} />
        <Route path="/admin/content/photos" element={<AdminRoute title="Photos and files"><AdminGuard><MediaLibrary /></AdminGuard></AdminRoute>} />
        <Route path="/admin/content/:collection" element={<AdminRoute title="Site content"><AdminGuard><CollectionScreen /></AdminGuard></AdminRoute>} />
        <Route path="/admin/content/:collection/new" element={<AdminRoute title="Site content"><AdminGuard><ItemEditorRoute isNew /></AdminGuard></AdminRoute>} />
        <Route path="/admin/content/:collection/:id" element={<AdminRoute title="Site content"><AdminGuard><ItemEditorRoute /></AdminGuard></AdminRoute>} />
        <Route element={<PublicLayout />}>
          <Route index element={<RouteTitle title={pageTitle("Art Therapy & Counseling in The Bahamas")}><Home /></RouteTitle>} />
          <Route path="/about-more" element={<RouteTitle title={pageTitle("About Jasmine G. Smith")}><AboutMore /></RouteTitle>} />
          <Route path="/policies" element={<RouteTitle title={pageTitle("Policies")}><Policies /></RouteTitle>} />
          <Route path="/faqs" element={<RouteTitle title={pageTitle("FAQs")}><FAQ /></RouteTitle>} />
          <Route path="/services/:slug" element={<ServiceRoute />} />
          <Route path="/resources" element={<RouteTitle title={pageTitle("Resources")}><Resources /></RouteTitle>} />
          <Route path="/in-home-art-therapy" element={<RouteTitle title={pageTitle("In-Home Art Therapy in Nassau")}><InHomeArtTherapy /></RouteTitle>} />
          <Route path="/packages" element={<RouteTitle title={pageTitle("Packages & Bundles")}><Packages /></RouteTitle>} />
          <Route path="/groups-and-workshops" element={<RouteTitle title={pageTitle("Groups & Workshops")}><GroupsWorkshops /></RouteTitle>} />
          <Route path="/organizations" element={<RouteTitle title={pageTitle("Organizational Facilitation")}><Organizations /></RouteTitle>} />
          <Route path="/schools" element={<RouteTitle title={pageTitle("Creative Wellness Program for Schools")}><Schools /></RouteTitle>} />
          <Route path="/checkout" element={<RouteTitle title={pageTitle("Book a Session")}><Checkout /></RouteTitle>} />
          <Route path="/booking-confirmation" element={<RouteTitle title={pageTitle("Booking Confirmation")}><BookingConfirmation /></RouteTitle>} />
          <Route path="*" element={<RouteTitle title={pageTitle("Page not found")}><NotFound /></RouteTitle>} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
